import React, { useState, useCallback, useRef } from 'react';
import { Alert, Stack, Box } from '@mui/material';
import { NotificationContext } from './useNotification';

const AUTO_HIDE_MS = 5000;
const MAX_NOTIFICATIONS = 5;

/**
 * NotificationProvider
 *
 * Provides a global notification system via React Context.
 *
 * Responsibilities:
 * - Manages a stack of notifications (max MAX_NOTIFICATIONS)
 * - Automatically dismisses each notification after AUTO_HIDE_MS
 * - Renders notifications as vertically stacked MUI Alerts
 * - Exposes a simple API (success, error, warning, info)
 *
 * Architectural notes:
 * - Does NOT use MUI Snackbar due to layout and lifecycle limitations
 * - Uses a fixed-position container and manual timers instead
 * - Each notification has an independent lifecycle
 *
 * @component
 * @param {object} props
 * @param {React.ReactNode} props.children - Application subtree
 * @returns {JSX.Element}
 */
export const NotificationProvider = ({ children }) => {
    /**
     * Active notifications.
     * Each entry has the shape:
     * { id: string, message: string, severity: 'success'|'error'|'warning'|'info' }
     */
    const [notifications, setNotifications] = useState([]);

    /**
     * Stores auto-dismiss timers per notification ID.
     * Map<notificationId, timeoutId>
     *
     * useRef is used to persist timers across renders without
     * triggering re-renders.
     */
    const timers = useRef(new Map());

    /**
     * Removes a notification by ID.
     *
     * - Updates state
     * - Clears and deletes the associated timer (if present)
     *
     * This function is idempotent and safe to call multiple times
     * for the same ID.
     *
     * @param {string} id - Notification identifier
     */
    const remove = useCallback((id) => {
        setNotifications((prev) =>
            prev.filter((n) => n.id !== id)
        );

        const timer = timers.current.get(id);
        if (timer) {
            clearTimeout(timer);
            timers.current.delete(id);
        }
    }, []);

    /**
     * Creates and displays a new notification.
     *
     * Behavior:
     * - Generates a unique ID
     * - Appends the notification to the stack
     * - Enforces MAX_NOTIFICATIONS by removing the oldest entry
     * - Schedules automatic dismissal after AUTO_HIDE_MS
     *
     * @param {string} message - Notification text
     * @param {'success'|'error'|'warning'|'info'} severity - Alert severity
     */
    const show = useCallback((message, severity) => {
        const id = crypto.randomUUID();

        setNotifications((prev) => {
            const next = [...prev, { id, message, severity }];

            // Enforce maximum stack size by removing the oldest notification
            if (next.length > MAX_NOTIFICATIONS) {
                const oldest = next[0];
                remove(oldest.id);
                return next.slice(1);
            }

            return next;
        });

        // Schedule automatic dismissal
        timers.current.set(
            id,
            setTimeout(() => remove(id), AUTO_HIDE_MS)
        );
    }, [remove]);

    /**
     * Public notification API exposed via context.
     *
     * Consumers should only interact with notifications through
     * these methods.
     */
    const api = {
        showSuccess: (msg) => show(msg, 'success'),
        showError: (msg) => show(msg, 'error'),
        showWarning: (msg) => show(msg, 'warning'),
        showInfo: (msg) => show(msg, 'info'),
    };

    return (
        <NotificationContext.Provider value={api}>
            {children}

            {/* Fixed-position notification container */}
            <Box
                sx={{
                    position: 'fixed',
                    bottom: 24,
                    right: 24,
                    zIndex: 1400,
                }}
            >
                {/* Vertical stack of notifications */}
                <Stack spacing={1} direction="column">
                    {notifications.map((n) => (
                        <Alert
                            key={n.id}
                            severity={n.severity}
                            variant="filled"
                            onClose={() => remove(n.id)}
                            sx={{ minWidth: 300 }}
                        >
                            {n.message}
                        </Alert>
                    ))}
                </Stack>
            </Box>
        </NotificationContext.Provider>
    );
};
